// Colours sampled from Kenney's colormap.png — the single atlas every model in
// the conveyor kit shares. The chess set is re-materialled into this palette
// rather than the reverse (PLAN §5.7.4): matching the smaller asset to the
// larger one needs no geometry work and yields one coherent look.
//
// Side identity is hue and value, never literal black and white. Pure black
// reads as a hole under a directional light.

export const PALETTE = {
  /**
   * White, and near enough neutral to read as the word.
   *
   * It was a warm ivory, which is what "white without blowing out" meant while
   * a side's colour was painted over a whole piece. Under paint mapping the
   * colour lands on the panels the art nominated and nothing else, so the warm
   * cast stopped being a safety margin and started being a claim — the white
   * army looked cream beside a black one. Same peak channel as the ivory it
   * replaces, so it is no more liable to blow out than that was.
   */
  lightPiece: 0xf2f4f7,
  /**
   * Black, deliberately not pure black. Pure black reads as a hole under a
   * directional light — it loses the shading that gives a piece its shape.
   */
  darkPiece: 0x24262d,

  // Squares sit between the two piece values so both sides keep contrast
  // against both colours of square.
  lightSquare: 0xc6cde2,
  darkSquare: 0x767ea3,
  boardEdge: 0x3e3e46,

  /** Kenney's industrial orange — the pawn chevron and other directional marks. */
  accent: 0xff9f43,

  /** Spoil piles: warm rust, so scrap reads as debris rather than as terrain. */
  /** A wreck: the piece that died here, greyed out. Side by tint, not shape. */
  wreckLight: 0x8f9099,
  wreckDark: 0x4a4b55,
  /** An artifact on the ground: gold, and nothing else on the board is. */
  artifact: 0xe8c46a,
  heap: 0xbe6d48,

  /**
   * A landing king's retro burn.
   *
   * Near-white rather than the orange everything else directional uses: the
   * flame is drawn additively, so it *adds* to whatever is behind it, and an
   * orange one over the board's blue-grey came out muddy brown. The hot core
   * of a flame is white and the colour is at its edges, which is what the
   * additive falloff over a cone gives for free.
   */
  thruster: 0xffe2b0,

  /**
   * Belts carry an owner colour, because both sides build and you must be able
   * to tell whose lane you are looking at — and whose you may crush. Kenney's
   * own conveyor material is near-black, which vanishes against the board, so
   * belts take the palette rather than the atlas: their *form* still comes from
   * the kit, only the colour is ours.
   */
  beltWhite: 0xe8a94f,
  beltBlack: 0x7b6fd6,

  /**
   * A building's crate. Deliberately a *material* colour rather than an
   * owner's: whose it is rides on the pad underneath, so the two questions —
   * what is this, and whose is it — get one answer each rather than sharing
   * one hue between them.
   */
  store: 0xb8b2a4,
  /**
   * A turret's laser.
   *
   * Hot rather than owner-coloured, deliberately: a beam lasts four tenths of
   * a second and what it has to say in that time is *this square was just
   * shot*, not whose gun did it — the gun is at one end of the line and wears
   * its owner's pad. Drawn additively, so this reads as the colour it adds to
   * whatever is behind it rather than as a surface.
   */
  beam: 0xff6a4a,

  /**
   * The chevron on a belt's deck. Dark, because it rides an owner-coloured
   * part and has to read against both sides' colours, and because accent
   * orange already means "pawn facing".
   */
  beltArrow: 0x2b2f3a,

  selection: 0xffc93c,
  legalMove: 0x5fcb8e,
  /**
   * Somewhere a *new piece appears*, as against somewhere one of yours walks.
   *
   * Cyan rather than another green, because the two are on the board at the
   * same moment — a king that is both a foundry and a piece offers its recipe's
   * destinations and its own moves in one click — and they were the same
   * marker in the same colour until now. The shape carries the distinction
   * (`boot` against `door_enter`) and this is the half that survives being
   * glanced at.
   */
  spawn: 0x4fd0e0,
  capture: 0xe13b3b,
  /**
   * A blow that will *not* kill (§27): the target keeps standing, loses the
   * attacker's damage and is shoved one square. Its own colour because red has
   * always meant "that piece comes off the board", and a bash drawn in it is
   * the board promising a capture it will not make. Amber — aggressive but
   * short of red, and used by no other highlight.
   */
  shove: 0xe8912e,
  /**
   * What just changed (§29 U4). One kind for a move, an undo and a history
   * step alike: a flat wash and no disc — "this is what changed" is one
   * sentence and it is the same sentence in all three cases. It **stays until
   * the next change replaces it**, which is what chess.com's last-move
   * highlight does and what the designer asked for by name.
   *
   * **`selection`'s gold, deliberately, and it was an olive-yellow of its
   * own.** The board already had a colour for "this square" and reaching past
   * it for `0xd8d24a` bought a second yellow the rest of the game does not
   * speak — chess.com runs one yellow for the selected square and the last
   * move, and so do we now (designer, 2026-09-10). What tells them apart is
   * what is *drawn*: selection carries a ring over a 0.24 wash, and this is a
   * bare wash at 0.5. Written as a hex rather than `PALETTE.selection`
   * because an object literal cannot read its own siblings.
   */
  changed: 0xffc93c,
  check: 0xf0442e,

  /**
   * Pawns a selected knight could re-aim. Deliberately far from legalMove
   * green: they sit on the board at the same time as the knight's own moves,
   * and clicking one does something completely different from moving there.
   */
  reAimTarget: 0xc45ce0,
  /**
   * Promotion ground. Gold, and the only highlight that is a property of the
   * *board* rather than of what you have selected — so it wants a colour
   * nothing else in the set uses, and it stays lit whether or not anything is
   * selected.
   */
  promotionSquare: 0xf2c14e,

  /**
   * The rest of the thing the selected square is part of — an ore vein's other
   * tiles, the squares a lane leads along.
   *
   * Its own colour because it is the only highlight that answers *what is
   * this* rather than *what may you do*: nothing is offered on these squares,
   * so wearing `legalMove`, `spawn`, `capture` or `selection` would be the
   * board promising a click that does nothing. A pale lilac — a hue no other
   * mark uses, and far quieter than `reAimTarget`'s purple, which is an
   * affordance — calm enough to read over both the rust and grey ground
   * washes (`lines`) without competing with the field it describes.
   */
  extent: 0xcdb8f0,

  /**
   * The direction picker's arrows. Not `accent`, though both are directional
   * marks: accent is the *persistent* language of facing — pawn chevrons, belt
   * arrows — and the picker is a transient control asking a question. Sharing
   * the hue read as more facing marks over the pieces (PLAN §7.1's arrow
   * soup), so the picker takes the selection colour instead: the same yellow
   * that means "this is the thing you are choosing".
   */
  picker: 0xffc93c,

  background: 0x22242c,
  ambient: 0xd3e4fb,

  /**
   * Armies past the classic two, in arrival order.
   *
   * **Named colours, and that is what paint mapping bought.** These used to be
   * muted — a dusty red, a slate blue, an olive — because a side's colour was
   * applied to the *whole* of a piece, so a saturated one buried the baked map
   * under a wash and every army came out as a coloured mass. The colour now
   * lands on the panels the artist painted white and leaves the copper, rust
   * and lights alone, which is about a fifth of the model: at that share a
   * muted colour says nothing at all, and a primary reads as an army from
   * across the board.
   *
   * So they are the words a player would use — red, green, blue, purple,
   * yellow, orange, brown, teal — rather than points chosen off a wheel. Value
   * is back in the set too: white is white and black is black, which a fade
   * could not do (see `TINT_VALUE`) and an override can.
   *
   * **Longer than the eight sides `Side` can hold**, on purpose. Six of these
   * are reachable today (sides 2-7); the tail is headroom, and headroom is
   * cheaper than the wrap it replaces — every army on a board is its own
   * colour with nothing repeating.
   */
  extraPieces: [
    0xe03b30, // red
    0x35b84a, // green
    0x1f7ae8, // blue — well clear of the board's own muted blue-grey
    0x9b46e0, // purple
    0xf5c518, // yellow
    0xf2760f, // orange
    0x8a5630, // brown
    0x1fc8d8 // teal, the other blue
  ],

  /**
   * The grade ladder as metals: iron, silver, gold, diamond.
   *
   * Chosen to separate by *value* as well as hue — dull grey, bright grey,
   * warm yellow, pale cyan — so the rung reads even where two of them sit on
   * the same square, and so the order is guessable without a legend.
   */
  // Five now: iron, steel, crucible steel, electrum, diamond (§26 F2). The
  // stone and special lines borrow these by rung for the moment — per-line
  // ingot art is the parked material-strip pass, not F2.
  grades: [0x8c7a68, 0xc9d2dc, 0xe8b53a, 0xd9c9a3, 0x7fe4e0],

  /**
   * What a material **line** looks like on the ground — metal, stone, special
   * (§26 F3), indexed by `MaterialLine`.
   *
   * Ore fields are drawn twice over: a faint wash on the deck saying which
   * tiles are one deposit, and a scatter of lumps on top counting what is
   * left. Both take their colour from here, so a field cannot say one thing
   * with its floor and another with its rocks.
   *
   * Separated by hue *and* value — rust, cool grey, pale gold — for `grades`'
   * own reason: two fields can be side by side on a grown board, and a player
   * has to tell iron from cobble across the width of it without a legend.
   */
  lines: [0xb0704a, 0x8d97a6, 0xe3c878, 0x2e2b2a],

  /**
   * A zone's ground wash (§31 D2), the doc's colours: Field green,
   * Battlefield grey, Quarry dark grey, Deposit light grey, Golden gold,
   * Diamond cyan. Drawn where no deposit's own wash is.
   */
  zones: {
    field: 0x5f9e4a,
    battlefield: 0x6e6a66,
    quarry: 0x4a4d52,
    deposit: 0xb9bcc2,
    golden: 0xe0b33a,
    diamond: 0x4fe3f0,
    void: 0x000000,
    canyon: 0x9a6b44,
    "broken-lands": 0x7a6f8f,
    "treasure-room": 0xf2d36b
  } as Record<string, number>,

  /**
   * What a **unit** of each line looks like, by rung — the piles a piece
   * holds, the cargo on a lane, the metal lying loose (§26 F3, iteration 3).
   *
   * `grades` is the metal ladder and stays exactly what it was, so no board
   * without stone on it changes by a pixel. The other two are the same idea
   * one line over: a rung reads as *brighter and bigger is better*, and the
   * family says which material it is. Cobble hauled across a board used to be
   * drawn in iron's rust — reported from a run as harvesting no stone at all,
   * when the stone was in hand and unrecognisable.
   *
   * Tables rather than arithmetic at the call sites, for `grades`' own reason:
   * a colour somebody chose survives, and a derived one drifts the first time
   * two of them have to sit side by side on one square.
   */
  stoneGrades: [0x6f7a88, 0x9aa6b4, 0xd7dee6],
  specialGrades: [0xe3c878, 0x9fe8dd],
  /** Fuel (§31 M1): coal near-black, nuclear a hot green. */
  fuelGrades: [0x34302d, 0x7be36b]
} as const;

/**
 * What army *n* looks like, as one colour.
 *
 * **One answer to "whose is that", for everything an army owns.** A player asks
 * it of a piece, of a lane, and of a building's pad, and until this existed the
 * three were answered by three different tables — pieces from `extraPieces`,
 * lanes from a pair of belt hues that ran out after two armies, pads from the
 * same pair. On a board with three camps on it the only way to tell whose track
 * you were looking at was to click it.
 *
 * Sides 0 and 1 are the pieces' own colours, deliberately: a lane should look
 * like the army that laid it, and the old belt hues were a separate identity
 * for the same thing. `PALETTE.beltWhite` and `beltBlack` survive as the
 * *material* the kit is re-coloured from — see their note about Kenney's
 * near-black conveyor vanishing against the board — but they are no longer
 * what says whose it is.
 *
 * It wraps rather than running out: `MAX_SIDES` is eight and the palette is
 * six, and a lane drawn in `undefined` is an invisible lane.
 */
export const armyColor = (side: number): number => {
  if (side === 0) return PALETTE.lightPiece;
  if (side === 1) return PALETTE.darkPiece;
  return PALETTE.extraPieces[(side - 2) % PALETTE.extraPieces.length];
};

export type PaletteColor = (typeof PALETTE)[keyof typeof PALETTE];
